import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Button, ButtonPrimary} from '@homeaway/react-buttons';
import './ExpediaAuthComponent.less';
import authAccountTypes from '../../enums/AuthAccountTypes';
import umsSettings from '../../util/ums.settings';
import {apiPost} from '../../util/api.client';
import {TrackingContext} from '@homeaway/react-event-tracking';
import aamClient from '../../util/aamClient';
import {isProductionEnvironment} from '../../util/dynamic.load';
import {getSiteHeader} from '../../util/siteHeaderUtils';

class ExpediaAuthComponent extends Component {
    static get propTypes() {
        return {
            enableLoading: PropTypes.func,
            disableLoading: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            enableLoading: () => {},
            disableLoading: () => {}
        };
    }

    static getExpediaUrl() {
        return isProductionEnvironment() ? 'https://www.expedia.com' : 'https://wwwexpediacom.trunk.sb.karmalab.net';
    }

    static getClientId() {
        return isProductionEnvironment() ? '4F4EBDFEF9E04DDCD429EABC47FA4918' : '67DE0287BF47F004473658373E250308';
    }

    static getScreenWidth() {
        let screenWidth = screen.width;
        if (window.innerWidth) {
            screenWidth = window.innerWidth;
        } else if (document.documentElement.clientWidth) {
            screenWidth = document.documentElement.clientWidth;
        }
        return screenWidth;
    }

    static getScreenHeight() {
        let screenHeight = screen.height;
        if (window.innerHeight) {
            screenHeight = window.innerHeight;
        } else if (document.documentElement.clientHeight) {
            screenHeight = document.documentElement.clientHeight;
        }
        return screenHeight;
    }

    static createId(length) {
        let text = '';
        let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789';

        let values = new Uint32Array(length);
        const cryptoObj = window.crypto || window.msCrypto;
        cryptoObj.getRandomValues(values);

        for (let i = 0; i < values.length; i++) {
            text += possible.charAt(values[i] % possible.length);
        }

        return text;
    }

    constructor(props) {
        super(props);
        // Window doesn't exist server side so this is needed, we need to pass a valid host name or component will not load in catalyst app!
        const hostPath = (typeof window !== 'undefined') ? window.location.origin : 'http://localhost:8080';
        this.redirectUri = `${hostPath}/auth/ui/expediaLogin`;
        this.state = {
            expediaWindow: null
        };
        // creating a funky guuid because uuid support in js is weak
        this.stateParam = ExpediaAuthComponent.createId(32);
    }

    componentDidMount() {
        if (this.props.token) {
            this.signUpAttempt();
            this.register(this.props.token);
        }
        window.addEventListener('message', this.handleResponse);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.handleResponse);
    }

    loginAttempt = () => {
        this.context.trigger('login.attempt', {logintype: 'expedia', loginsource: 'lex'});
    };

    loginSuccess = (json) => {
        this.context.trigger('login.success', {logintype: 'expedia', loginsource: 'lex', publicuuid: json.publicUuid});
        this.authSuccess(json);
    };

    loginFailure = () => {
        this.setState({loggedIn: false});
        this.context.trigger('login.failure', {logintype: 'expedia', loginsource: 'lex'});
    };

    displayErrorMessage = (error, errorTitle) => {
        this.props.handleError(error, errorTitle);
    };

    authSuccess = (json) => {
        this.setState({loggedIn: true});
        this.props.setAuthJson(json);
        this.props.onClose();
    };

    signUpSuccess = (json) => {
        if (this.props.marketingOptOut !== null) {
            this.context.trigger('user.account.created', {
                logintype: 'expedia',
                loginsource: 'lex',
                newusertype: 'traveler',
                marketingoptout: this.props.marketingOptOut,
                publicuuid: json.publicUuid
            });
        } else {
            this.context.trigger('user.account.created', {
                logintype: 'expedia',
                loginsource: 'lex',
                newusertype: 'traveler',
                publicuuid: json.publicUuid
            });
        }
        // set logged in state and close modal
        this.authSuccess(json);
    };

    signUpFailure = (statusCode) => {
        this.setState({loggedIn: false});
        if (this.props.marketingOptOut !== null) {
            this.context.trigger('user.account.creation.failure', {
                logintype: 'expedia',
                loginsource: 'lex',
                failurereason: `${statusCode}`,
                marketingoptout: this.props.marketingOptOut,
                newusertype: 'traveler'
            });
        } else {
            this.context.trigger('user.account.creation.failure', {
                logintype: 'expedia',
                loginsource: 'lex',
                failurereason: `${statusCode}`,
                newusertype: 'traveler'
            });
        }
    };

    signUpAttempt() {
        if (this.props.marketingOptOut !== null) {
            this.context.trigger('user.account.creation.attempt', {
                logintype: 'expedia',
                loginsource: 'lex',
                marketingoptout: this.props.marketingOptOut,
                newusertype: 'traveler'
            });
        } else {
            this.context.trigger('user.account.creation.attempt', {
                logintype: 'expedia',
                loginsource: 'lex',
                newusertype: 'traveler'
            });
        }
    }

    expediaGenericEdap = (message) => {
        this.context.trigger('generic.event', {eventaction: `lexExpediaAuth.${message}`, eventcategory: 'lexExpediaAuth'});
    };

    handleJson(json, accessToken) {
        if (json) {
            if (authAccountTypes.isNotUndefined(json.authType)) {
                let SIGNUP = this.checkAuthType(json.authType, authAccountTypes.SIGNUP);
                if (SIGNUP && umsSettings.isTCRequired(this.props.site)) {
                    // this is the SignUp scenario, so we need to update the pageName to TermsAndConditions only for the required EU brands
                    let pageName = 'TermsAndConditions';
                    this.props.performBulkPageUpdate(pageName, '', authAccountTypes.EXPEDIA, accessToken);
                    this.props.disableLoading();
                } else {
                    let umsOnly = this.checkAuthType(json.authType, authAccountTypes.UMS);
                    if (SIGNUP || umsOnly) {
                        this.signUpAttempt();
                        this.register(accessToken);
                    } else {
                        this.loginAttempt();
                        this.authenticate(accessToken);
                    }
                }
            }
        }
    }

    checkAuthType(accountType, loginType) {
        return accountType.some((t) => (t === loginType));
    }

    expediaAuthStatus(accessToken) {
        const payload = {
            accessToken,
            authType: 'EXPEDIA'
        };

        return new Promise((resolve, reject) => {
            apiPost('/auth/aam/v3/token/status', payload).then((response) => {
                if (response.status >= 200 && response.status < 400) {
                    response.json().then((json) => {
                        this.handleJson(json, accessToken);
                        return resolve();
                    });
                }
            }).catch((e) => {
                this.expediaGenericEdap('expediaResponseError');
                // If user denied or something else failed we log it, except if they just close pop up, no error should be displayed then
                if (typeof e === 'undefined' || e.error !== 'popup_closed_by_user') {
                    this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
                }
                return reject(e);
            });
        });
    }

    expediaAccessToken(code) {
        const payload = {
            authCode: `${this.redirectUri};${code}`
        };

        return new Promise((resolve, reject) => {
            apiPost('/auth/aam/v3/expedia/token', payload).then((response) => {
                if (response.status >= 200 && response.status < 400) {
                    response.json().then((json) => {
                        this.expediaAuthStatus(json.accessToken);
                        return resolve('expedia access token resolved');
                    });
                } else {
                    this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
                }
            }).catch((e) => {
                this.expediaGenericEdap('accessTokenError');
                // If user denied or something else failed we log it, except if they just close pop up, no error should be displayed then
                if (typeof e === 'undefined' || e.error !== 'popup_closed_by_user') {
                    this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
                }
                return reject(e);
            });
        });
    }

    register(token) {
        const payload = {
            userName: '',
            secret: token,
            authType: 'EXPEDIA',
            rememberMe: true
        };
        return new Promise((resolve, reject) => {
            apiPost('/auth/aam/v3/register', payload, getSiteHeader(this.props.site)).then((response) => {
                this.checkStatus(response, resolve, reject, true);
            }).catch((e) => {
                return this.failLogin(e, reject, true);
            });
        });
    }

    authenticate(token) {
        const payload = {
            'userName': '',
            'authType': 'EXPEDIA',
            'secret': token,
            'rememberMe': true
        };
        return new Promise((resolve, reject) => {
            aamClient.authenticate(payload).then((response) => {
                this.checkStatus(response, resolve, reject, false);
            }).catch((e) => {
                this.failLogin(e, reject, false);
            });
        });
    }

    failLogin(e, reject, isRegister) {
        this.props.disableLoading();
        if (isRegister === true) {
            this.signUpFailure(e);
        } else {
            this.loginFailure();
        }
        this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
        return reject(e);
    }

    checkStatus(response, resolve, reject, isRegister) {
        if (response.status >= 200 && response.status < 400) {
            response.json().then((json) => {
                if (isRegister === true) {
                    this.signUpSuccess(json);
                } else {
                    this.loginSuccess(json);
                }
            });
            return resolve();
        }
        if (isRegister === true) {
            this.signUpFailure(response.status);
        } else {
            this.loginFailure();
        }
        this.props.disableLoading();
        this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
        return reject();
    }

    onClickExpedia = () => {
        this.expediaGenericEdap('expediaButtonClicked');
        this.expediaSignInSignUp();
    }

    expediaSignInSignUp() {
        let expediaAuthWindow = this.openNewSocialAuthWindow(425, 450);
        this.setState({expediaWindow: expediaAuthWindow});
    }

    handleResponse = (message) => {
        if (!this.isExpediaSuccessRedirectMessage(message)) {
            return;
        }
        let code = message.data.code;
        let expediaState = message.data.state;
        if (code !== '' && expediaState === this.stateParam) {
            this.expediaAccessToken(code);
        } else {
            this.expediaGenericEdap('ExpediaAuthCodeError');
            this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
        }
    };

    isExpediaSuccessRedirectMessage(message) {
        const acceptedOrigin = window.location.origin;
        const acceptedAction = 'expediaSuccessRedirect';
        return acceptedOrigin === message.origin
            && message.source.location.href.includes('auth/ui/expediaLogin')
            && acceptedAction === message.data.action;
    }

    openNewSocialAuthWindow(width, height) {
        let dualScreenLeft = window.screenLeft !== 'undefined' ? window.screenLeft : screen.left;
        let dualScreenTop = window.screenTop !== 'undefined' ? window.screenTop : screen.top;
        let screenWidth = ExpediaAuthComponent.getScreenWidth();
        let screenHeight = ExpediaAuthComponent.getScreenHeight();
        let left = ((screenWidth / 2) - (width / 2)) + dualScreenLeft;
        let top = ((screenHeight / 2) - (height / 2)) + dualScreenTop;
        let url = `${ExpediaAuthComponent.getExpediaUrl()}/oauth/authorize?client_id=${ExpediaAuthComponent.getClientId()}&response_type=code&redirect_uri=${this.redirectUri}&state=${this.stateParam}`;

        return window.open(
            url,
            '',
            `channelmode=yes,width=${ width },height=${ height },top=${ top }, left=${ left}`,
            'resizable=no,toolbar=no,menubar=no,location=no,status=no');
    }

    displayAppropriateButton() {
        if (typeof this.props.authStatus !== 'undefined') {
            return (
                <div className="SocialButtons__icon">
                    <ButtonPrimary label="" block className="btn--expedia btn-block" onClick={this.onClickExpedia} name={this.props.name} id={this.props.id}>
                        <span className="Expedia_icon SVGIcon--24px SVGIcon--inline-flex btn--spacing">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="34" viewBox="0 0 22 34" preserveAspectRatio="xMaxYMax meet">
                                <g fill="none" fillRule="evenodd">
                                    <path fill="#EDB32A" d="M14.217 19.438l7.18-12.175c-.118-.336-.25-.666-.4-.99L8.285.905.151 12.55c.03.188.062.377.1.564l13.966 6.324"/>
                                    <path fill="#212D4C"
                                        d="M12.847 8.452L12.405 7.2 8.653 2.175l.674-.168 6.47 5.602 3.806-1.091c.447-.115.954-.14 1.342-.137A10.962 10.962 0 0 0 14.67.64C8.954-1.4 2.67 1.592.638 7.32a10.994 10.994 0 0 0-.514 5.338l9.12-3.174 3.603-1.032M21.378 7.459c-.336.213-.826.5-1.315.654l-3.82 1.082-2.55 8.127-.661.215.539-6.208-.302-1.288-3.58 1.015-9.463 2.212a10.947 10.947 0 0 0 7.086 8.096c5.724 2.025 12.012-.946 14.05-6.628.876-2.448.82-5.002.016-7.277"
                                    />
                                </g>
                            </svg>
                        </span>
                        <span className="Expedia_button_label">{this.props.lingua.read('lex.social.login.with.card.message.expedia')}</span>
                    </ButtonPrimary>
                </div>
            );
        }
        if (this.props.expediaIconDisplayed === true) {
            return (
                <div className="SocialButtons_icon">
                    <div className="small">
                        <Button type="button" className="btn btn-icon btn-default btn-icon-circle" onClick={this.onClickExpedia} label="" name={this.props.name} id={this.props.id}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="34" viewBox="0 0 22 34" preserveAspectRatio="xMaxYMax meet">
                                <g fill="none" fillRule="evenodd">
                                    <path fill="#EDB32A"
                                        d="M14.217 19.438l7.18-12.175c-.118-.336-.25-.666-.4-.99L8.285.905.151 12.55c.03.188.062.377.1.564l13.966 6.324"
                                    />
                                    <path fill="#212D4C"
                                        d="M12.847 8.452L12.405 7.2 8.653 2.175l.674-.168 6.47 5.602 3.806-1.091c.447-.115.954-.14 1.342-.137A10.962 10.962 0 0 0 14.67.64C8.954-1.4 2.67 1.592.638 7.32a10.994 10.994 0 0 0-.514 5.338l9.12-3.174 3.603-1.032M21.378 7.459c-.336.213-.826.5-1.315.654l-3.82 1.082-2.55 8.127-.661.215.539-6.208-.302-1.288-3.58 1.015-9.463 2.212a10.947 10.947 0 0 0 7.086 8.096c5.724 2.025 12.012-.946 14.05-6.628.876-2.448.82-5.002.016-7.277"
                                    />
                                </g>
                            </svg>
                            <span className="sr-only">{'Expedia'}</span>
                        </Button>
                        <div className="Expedia_label">
                            <label>{this.props.lingua.read('lex.expedia.icon.label')}</label>
                        </div>
                    </div>
                </div>
            );
        }
        return (<div/>);
    }

    render() {
        return (
            <div>
                {this.displayAppropriateButton()}
            </div>
        );
    }
}

ExpediaAuthComponent.contextType = TrackingContext;
export default ExpediaAuthComponent;
